import axios from "axios";

export const TOKEN_KEY = "equipr_token";

// Attach/detach the JWT session token on all axios requests.
export function applyAuthToken(token) {
  if (token) {
    axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    localStorage.setItem(TOKEN_KEY, token);
  } else {
    delete axios.defaults.headers.common["Authorization"];
    localStorage.removeItem(TOKEN_KEY);
  }
}

// Decode a JWT payload (no verification — client-side expiry check only).
export function decodeJwt(token) {
  try {
    const part = (token || "").split(".")[1];
    if (!part) return null;
    const json = atob(part.replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(json);
  } catch (e) {
    return null;
  }
}

// True when the token is missing or its `exp` claim has passed (10s skew).
export function isTokenExpired(token) {
  if (!token) return true;
  const payload = decodeJwt(token);
  if (!payload || !payload.exp) return false; // no exp claim → don't force logout
  return Date.now() >= payload.exp * 1000 - 10000;
}

// Message left for the Login screen to show after an involuntary bounce, so a
// session that expired mid-action (e.g. part-way through an upload) explains
// itself instead of looking like the action silently failed.
export const LOGIN_NOTICE_KEY = "equipr_login_notice";

// Clear the session and bounce to login (guarded against redirect loops).
export function forceLogin(notice) {
  if (window.__equipr_redirecting) return;
  window.__equipr_redirecting = true;
  applyAuthToken(null);
  localStorage.removeItem("equipr_session");
  if (notice) sessionStorage.setItem(LOGIN_NOTICE_KEY, notice);
  window.location.href = "/login";
}

// Restore the token header from localStorage on app load.
export function initAuthToken() {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token && !isTokenExpired(token)) {
    axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  } else if (token) {
    // Stale/expired token left over from a previous session — clear it now so
    // route guards send the user to Login immediately instead of rendering an
    // empty shell whose every API call silently 401s.
    applyAuthToken(null);
    localStorage.removeItem("equipr_session");
  }

  // Any 401 from a gated API means our token is missing/expired → go to Login
  // for a fresh session instead of a silent, data-less screen. Auth endpoints
  // (login / OTP) are excluded to avoid a redirect loop while signing in.
  axios.interceptors.response.use(
    (res) => res,
    (err) => {
      const url = err?.config?.url || "";
      const status = err?.response?.status;
      const isApi = /\/api\//.test(url);
      // Auth flows and the PUBLIC client-dashboard endpoints handle their own
      // 401s in-page. Redirecting them to the staff /login is wrong — it bounced
      // client-dashboard users to the EQUIPR Intelligence login after entering
      // their access code.
      const isAuthFlow = /\/api\/auth\/staff\/(request-otp|verify-otp)/.test(url);
      const isClientDashboard = /\/api\/client-dashboards\//.test(url);
      if (status === 401 && isApi && !isAuthFlow && !isClientDashboard) {
        const detail = err?.response?.data?.detail || "";
        forceLogin(
          /no longer active/i.test(detail)
            ? "This EQUIPR account is no longer active. Contact your administrator if you think that is wrong."
            : "Your session expired, so that last action did not go through. Log in and try it again."
        );
      }
      return Promise.reject(err);
    }
  );

  // Ask the server whether this session is still allowed. A token can be
  // unexpired yet belong to an account that has since been disabled — the
  // 401 from this call bounces them to Login on the spot rather than letting
  // a stale localStorage session render the app shell.
  if (localStorage.getItem(TOKEN_KEY)) {
    const base = process.env.REACT_APP_BACKEND_URL;
    axios.get(`${base}/api/auth/me`).catch(() => {});
  }
}
